import { useTranslation } from "react-i18next";
import {
  SectionHeader,
  SectionTitle,
  EditButton,
  InputContainer,
  CustomInput,
  InputIcon,
  DiscountInputContainer,
  FormLabel,
  DiscountDescription,
  CancelButton,
  ButtonContainerStyled,
} from "../styles";
import { useState } from "react";
import { IPropertyDiscounts } from "../types";
import {
  UseFormRegister,
  Control,
  Controller,
  UseFormTrigger,
} from "react-hook-form";

interface DiscountSectionProps {
  discounts?: {
    weekly?: number;
    monthly?: number;
  };
  onSave: () => void;
  register: UseFormRegister<IPropertyDiscounts>;
  control: Control<IPropertyDiscounts>;
  reset: () => void;
  trigger: UseFormTrigger<IPropertyDiscounts>;
}

const DiscountSection = ({
  discounts,
  onSave,
  register,
  control,
  reset,
  trigger,
}: DiscountSectionProps) => {
  const { t } = useTranslation();
  const [editDiscounts, setEditDiscounts] = useState(false);

  const DiscountsInputs = [
    {
      id: 1,
      labelText: t("PROPERTIES_MANAGEMENT.WEEKLY"),
      inputIcon: <span>%</span>,
      defaultValue: discounts?.weekly?.toString() || "",
      name: "weeklyDiscount",
    },
    {
      id: 2,
      labelText: t("PROPERTIES_MANAGEMENT.MONTHLY"),
      inputIcon: <span>%</span>,
      defaultValue: discounts?.monthly?.toString() || "",
      name: "monthlyDiscount",
    },
  ];

  return (
    <>
      {/*  Discounts section  */}
      <SectionHeader>
        <SectionTitle>{t("PROPERTIES_MANAGEMENT.DISCOUNTS")}</SectionTitle>
        {!editDiscounts && (
          <EditButton onClick={() => setEditDiscounts(true)}>
            {t("PROPERTIES_MANAGEMENT.EDIT")}
          </EditButton>
        )}
        {editDiscounts && (
          <ButtonContainerStyled>
            <EditButton
              onClick={() => {
                reset();
                setEditDiscounts(false);
              }}
            >
              {t("COMMON.CANCEL")}
            </EditButton>
            <CancelButton
              onClick={async () => {
                // Trigger validation for all discount fields
                const isValid = await trigger([
                  "weeklyDiscount",
                  "monthlyDiscount",
                ]);

                if (isValid) {
                  onSave();
                  setEditDiscounts(false);
                }
              }}
            >
              {t("COMMON.SAVE")}
            </CancelButton>
          </ButtonContainerStyled>
        )}
      </SectionHeader>

      {DiscountsInputs?.map((input) => (
        <InputContainer key={input.id}>
          <FormLabel>{input.labelText}</FormLabel>

          <DiscountInputContainer style={{ position: "relative" }}>
            <Controller
              name={input.name as keyof IPropertyDiscounts}
              control={control}
              rules={{
                required: t("PROPERTIES_MANAGEMENT.THIS_FIELD_IS_REQUIRED"),
                min: {
                  value: 0,
                  message: t("PROPERTIES_MANAGEMENT.DISCOUNT_MIN_VALUE"),
                },
                max: {
                  value: 100,
                  message: t("PROPERTIES_MANAGEMENT.DISCOUNT_MAX_VALUE"),
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <CustomInput
                    {...field}
                    disabled={!editDiscounts}
                    type="number"
                    value={field.value || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? "" : Number(value));
                    }}
                  />
                  {error && (
                    <div
                      style={{
                        top: "10px",
                        position: "absolute",
                        transform: "translateY(50px)",
                        color: "#d32f2f",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {error.message}
                    </div>
                  )}
                </>
              )}
            />
            <InputIcon>{input.inputIcon}</InputIcon>
          </DiscountInputContainer>
          <DiscountDescription>
            {t("PROPERTIES_MANAGEMENT.WEEKLY_DISCOUNT_DESCRIPTION")}
          </DiscountDescription>
        </InputContainer>
      ))}
    </>
  );
};

export default DiscountSection;
